import type { ZayacPhase } from './zayac-phases';

export type ZayacRoomState = 'lobby' | 'running' | 'finished' | 'abandoned';

export type ZayacConnectionStatus = 'not_joined' | 'connected' | 'disconnected' | 'replaced';

export interface ZayacPlayerPublic {
  playerId: string;
  name: string;
  status: ZayacConnectionStatus;
  isCreator: boolean;
  score: number;
  ready: boolean;
}

export interface ZayacGameSettings {
  rounds: number;
  deckId: string;
  turnSeconds: number;
  discussionSeconds: number;
  votingSeconds: number;
  revealSeconds: number;
  selfVotingAllowed: boolean;
  avoidRepeatZayac: boolean;
  audioEnabled: boolean;
  manualVoting: boolean;
}

export const DEFAULT_ZAYAC_SETTINGS: ZayacGameSettings = {
  rounds: 5,
  deckId: 'default-ru',
  turnSeconds: 20,
  discussionSeconds: 60,
  votingSeconds: 30,
  revealSeconds: 8,
  selfVotingAllowed: false,
  avoidRepeatZayac: true,
  audioEnabled: false,
  manualVoting: false,
};

export interface ZayacLobbyView {
  roomId: string;
  code: string;
  joinUrl: string;
  qrPayload: string;
  state: ZayacRoomState;
  players: ZayacPlayerPublic[];
  canStart: boolean;
  settings: ZayacGameSettings;
  minPlayers: number;
  maxPlayers: number;
}

export interface ZayacRoomSummary {
  roomId: string;
  code: string;
  state: ZayacRoomState;
  joinUrl: string;
  qrPayload: string;
  roundNumber: number;
  phase: ZayacPhase;
  winnerPlayerId: string | null;
}

export interface ZayacVoteTotal {
  playerId: string;
  votes: number;
}

export interface ZayacPlayerTarget {
  playerId: string;
  name: string;
}

export type ZayacScreen =
  | 'lobby'
  | 'secret' // you see the word
  | 'zayac' // you are the zayac (no word)
  | 'your_turn' // association input
  | 'listening' // someone else speaks
  | 'discussion'
  | 'voting'
  | 'reveal'
  | 'round_end'
  | 'game_over'
  | 'waiting';

export interface ZayacPrivateState {
  screen: ZayacScreen;
  phase: ZayacPhase;
  roundNumber: number;
  isCreator: boolean;
  endsAt: string | null;
  /** Only set for non-zayac players during the initial secret distribution. */
  secretWord: string | null;
  /** Only set on the zayac screen / after reveal. */
  isZayac: boolean;
  voteTotals: ZayacVoteTotal[];
  /** Players available to vote for (excludes self when self-voting disabled). */
  voteTargets: ZayacPlayerTarget[];
  selectedVoteId: string | null;
  scoreboard: { playerId: string; name: string; score: number }[];
  /** Active speaker during ASSOCIATION. */
  speaker: ZayacSpeakerTurn | null;
  /** Themed word card shown only during the initial secret distribution. */
  cardCategory: string | null;
  cardWords: string[];
  /** Reveal data — only populated during REVEAL / ROUND_END. */
  reveal: ZayacReveal | null;
  /** Manual voting mode: discussion merged into voting, host ends manually. */
  manualVoting: boolean;
  /** How many players have cast a vote in the current VOTING phase. */
  votingSubmittedCount: number;
  /** Total eligible voters in the current VOTING phase. */
  votingPlayerCount: number;
  text: string | null;
}

export interface ZayacSpeakerTurn {
  viewerPlayerId: string;
  playerId: string;
  name: string;
  isYou: boolean;
  isZayac: boolean; // never leaks: always false during ASSOCIATION
  index: number;
  total: number;
  speakers: ZayacPlayerTarget[];
  turnStartedAt: string;
  turnEndsAt: string | null;
}

export interface ZayacReveal {
  zayacPlayerId: string;
  zayacName: string;
  secretWord: string;
  topVotedPlayerId: string | null;
  topVotedName: string | null;
  caught: boolean;
  tied: boolean;
  roundPointsAwarded: boolean;
}

export interface ZayacGameHistorySummary {
  gameId: string;
  roomId: string;
  startedAt: string;
  finishedAt: string | null;
  winnerPlayerId: string | null;
  playerCount: number;
  rounds: number;
}

export const ZAYAC_MIN_PLAYERS = 3;
export const ZAYAC_MAX_PLAYERS = 12;
export const ZAYAC_RECOMMENDED_MIN = 3;
export const ZAYAC_RECOMMENDED_MAX = 8;
